import {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react'

export interface Track {
  id: string | number
  title: string
  subtitle?: string
  src: string
}

interface AudioContextValue {
  current: Track | null
  isPlaying: boolean
  currentTime: number
  duration: number
  play: (track: Track) => void
  toggle: () => void
  seek: (time: number) => void
  /** This id is the loaded track (playing OR paused). */
  isCurrent: (id: string | number) => boolean
  /** This id is the loaded track AND currently playing. */
  isActive: (id: string | number) => boolean
}

const AudioCtx = createContext<AudioContextValue | null>(null)

export function useAudio(): AudioContextValue {
  const ctx = useContext(AudioCtx)
  if (!ctx) throw new Error('useAudio must be used within <AudioProvider>')
  return ctx
}

function fmt(t: number): string {
  if (!isFinite(t) || t < 0) return '0:00'
  const m = Math.floor(t / 60)
  const s = Math.floor(t % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

export function AudioProvider({ children }: { children: ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [current, setCurrent] = useState<Track | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [time, setTime] = useState(0)
  const [duration, setDuration] = useState(0)

  const play = useCallback(
    (track: Track) => {
      const audio = audioRef.current
      if (!audio) return
      if (current && current.id === track.id) {
        if (audio.paused) audio.play()
        else audio.pause()
        return
      }
      setCurrent(track)
      audio.src = track.src
      audio.currentTime = 0
      audio.play().catch(() => {})
    },
    [current],
  )

  const toggle = useCallback(() => {
    const audio = audioRef.current
    if (!audio || !current) return
    if (audio.paused) audio.play()
    else audio.pause()
  }, [current])

  const isCurrent = useCallback(
    (id: string | number) => !!current && current.id === id,
    [current],
  )

  const isActive = useCallback(
    (id: string | number) => !!current && current.id === id && isPlaying,
    [current, isPlaying],
  )

  const seek = useCallback((t: number) => {
    const audio = audioRef.current
    if (!audio) return
    audio.currentTime = t
    setTime(t)
  }, [])

  // Keyboard: space toggles when a track is loaded and focus isn't in a field.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.code !== 'Space' || !current) return
      const el = document.activeElement
      const tag = el?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (el as HTMLElement)?.isContentEditable) return
      e.preventDefault()
      toggle()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [current, toggle])

  function onSeek(e: React.ChangeEvent<HTMLInputElement>) {
    const audio = audioRef.current
    if (!audio) return
    audio.currentTime = Number(e.target.value)
    setTime(audio.currentTime)
  }

  const pct = duration ? (time / duration) * 100 : 0

  return (
    <AudioCtx.Provider
      value={{ current, isPlaying, currentTime: time, duration, play, toggle, seek, isCurrent, isActive }}
    >
      {children}

      <audio
        ref={audioRef}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
      />

      {/* Spotify-style fixed player bar */}
      <div
        className={`fixed bottom-0 inset-x-0 z-50 border-t border-line bg-ink-soft/95 backdrop-blur transition-transform duration-300 ${
          current ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 h-16 flex items-center gap-4">
          <button
            type="button"
            onClick={toggle}
            aria-label={isPlaying ? 'Pause' : 'Play'}
            className="shrink-0 size-10 rounded-full bg-paper text-ink grid place-items-center hover:scale-105 transition"
          >
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </button>

          <div className="shrink-0 min-w-0 w-40 sm:w-56">
            <div className="truncate text-sm font-medium">{current?.title}</div>
            <div className="truncate mono-label !text-[10px] mt-0.5">{current?.subtitle}</div>
          </div>

          <div className="flex-1 flex items-center gap-3">
            <span className="mono-label !text-[10px] tabular-nums w-9 text-right">{fmt(time)}</span>
            <div className="relative flex-1 h-1.5 group">
              <div className="absolute inset-0 rounded-full bg-line" />
              <div className="absolute inset-y-0 left-0 rounded-full bg-paper" style={{ width: `${pct}%` }} />
              <input
                type="range"
                min={0}
                max={duration || 0}
                value={time}
                onChange={onSeek}
                aria-label="Seek"
                className="absolute inset-0 w-full opacity-0 cursor-pointer"
              />
            </div>
            <span className="mono-label !text-[10px] tabular-nums w-9">{fmt(duration)}</span>
          </div>
        </div>
      </div>
    </AudioCtx.Provider>
  )
}

function PlayIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <path d="M4 2.5v11l9-5.5-9-5.5Z" />
    </svg>
  )
}
function PauseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <path d="M4 2.5h3v11H4zM9 2.5h3v11H9z" />
    </svg>
  )
}
